/**
 * T-128: renders the byline block as a Link when there is a real destination,
 * and as a plain <div> with IDENTICAL markup and classes when there is not.
 *
 * WHY A COMPONENT AND NOT A TERNARY AT EACH CALL SITE: the two feed cards both
 * had `to={post.source?.id ? '/source/'+id : '#'}` — the same wrong rule copied
 * twice. Two copies of a rule drift (T-127: the header and the sidebar derived
 * auth two different ways and shipped two auth states in one paint). One
 * wrapper means the "link or text" decision exists once.
 *
 * Decision 105: an option a user cannot complete must not be rendered. An
 * `href="#"` is worse than plain text — it looks clickable, takes the click,
 * and does nothing.
 */
import { Link } from 'react-router-dom';
import type { MouseEvent, ReactNode } from 'react';

interface BylineWrapProps {
  /** Destination, or null when this byline has no page to go to. */
  href: string | null;
  className?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  children: ReactNode;
}

export function BylineWrap({ href, className, onClick, children }: BylineWrapProps) {
  if (href) {
    return (
      <Link to={href} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  }

  // Unlinked: same box, same spacing, no hover-to-nowhere. onClick is still
  // wired because these cards sit inside a click-through card overlay and the
  // byline must not trigger the card's navigation either.
  return (
    <div className={className} onClick={onClick}>
      {children}
    </div>
  );
}
